import * as React from 'react';
import * as PXBCore from '@brightlayer-ui/react-components/core';
import * as PXBColors from '@brightlayer-ui/colors';
import { List, Card, Divider, Typography, ListSubheader, IconButton } from '@mui/material';
import { ActionRow } from '../OverviewCard/ActionRow';
import { AppRoute } from '../../routes';
import * as MuiIcons from '@mui/icons-material';
import { getIcon } from '../../lib/icons';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useHistory } from 'react-router-dom';
import { Data, isDataLoaded} from '../../types/data-providers';
import { Status } from '../../types';
import { StatusTag } from '../StatusTag/StatusTag';
import { DataIndicators } from '../DataIndicators';
type SiteDevicesCardDevice = {
    id: string;
    name: string;
    type: string | null;
    iconClass: string;
    status: boolean;
};

export const SiteDevicesCard: React.FC<{
    devicesData: Data<SiteDevicesCardDevice[]>;
    maxItemsToDisplay?: number;
    viewAllDevicesAppRoute?: AppRoute;
    deviceDetailAppRoute: AppRoute;
    style?: any;
}> = (props) => {
    const { t } = useLanguageLocale();
    const history = useHistory();
    const devicesToDisplay = isDataLoaded(props.devicesData)
        ? props.maxItemsToDisplay
            ? props.devicesData.value.slice(0, props.maxItemsToDisplay)
            : props.devicesData.value
        : [];

    // Don't show the divider if there is no View All link, and
    // if it's the last item in the list
    const itemDivider = (deviceIndex: number): 'full' | undefined =>
        props.viewAllDevicesAppRoute || deviceIndex !== devicesToDisplay.length - 1 ? 'full' : undefined;

    const navigateToDeviceDetail = (deviceData: SiteDevicesCardDevice): void => {
       
        if ('deviceId' in props.deviceDetailAppRoute) {                    
            pushAppRoute(history, {
                ...props.deviceDetailAppRoute,
                deviceId: deviceData.id,
            });           
        }
    };
     const isEmpty = devicesToDisplay.length === 0;

    return (
        <Card data-testid="site-devices-card" style={{ textDecorationSkipInk: 'inherit', ...props.style }}>
            <List
                subheader={
                    <>
                        <ListSubheader color="primary" component="div" style={{ fontWeight: 600 }}>
                            {t('DASHBOARD.SITE_DETAILS.DEVICES_CARD.TITLE')}
                        </ListSubheader>
                        <Divider />
                    </>
                }
                disablePadding
            >
                <DataIndicators
                    emptyIcon="Devices"
                    emptyMessage={t('DASHBOARD.NO_DATA')}
                    errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                    data={props.devicesData}
                    isEmpty={isEmpty}
                    inline
                />

                {devicesToDisplay.map((deviceData, index) => (
                    <div data-testid="device-list-item" key={index}>
                        <PXBCore.InfoListItem
                            title=""
                            backgroundColor={PXBColors.white[50]}
                            divider={itemDivider(index)}
                            icon={React.createElement(getIcon(deviceData.iconClass, 'DeviceUnknown'))}
                            iconColor={PXBColors.gray[500]}
                            onClick={(): void => navigateToDeviceDetail(deviceData)}
                            leftComponent={
                                <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                    <Typography
                                        variant="body1"
                                        display="inline"
                                        style={{ fontWeight: 600, lineHeight: 1.25 }}
                                    >
                                        {deviceData.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        display="block"
                                        style={{
                                            lineHeight: 1.25,
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {deviceData.type}
                                    </Typography>
                                </div>
                            }
                            rightComponent={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    
                                   { deviceData.status ? <StatusTag status={Status.Online}/> : 
                                     <StatusTag status={Status.Offline}/> }
                                   
                                    <IconButton style={{ marginLeft: 16 }} size="large">
                                        <MuiIcons.ChevronRight />
                                    </IconButton>
                                </div>
                            }
                        />
                    </div>
                ))}
            </List>

            {!isEmpty && props.viewAllDevicesAppRoute && (
                <ActionRow
                    label={t('DASHBOARD.SITE_DETAILS.DEVICES_CARD.VIEW_ALL')}
                    appRoute={props.viewAllDevicesAppRoute}
                />
            )}
        </Card>
    );
};
